<template>
  <nav class="kds-nav" aria-label="Primary">
    <div class="kds-nav__inner">
      <a class="kds-nav__brand" :href="brandHref">
        <span class="kds-nav__brand-mark" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="22" height="22" fill="none">
            <circle cx="32" cy="32" r="22" stroke="currentColor" stroke-width="3.5" fill="none" />
            <circle cx="32" cy="10" r="2.5" fill="currentColor" />
            <circle cx="51" cy="20" r="2.5" fill="currentColor" />
            <circle cx="51" cy="44" r="2.5" fill="currentColor" />
            <circle cx="32" cy="54" r="2.5" fill="currentColor" />
            <circle cx="13" cy="44" r="2.5" fill="currentColor" />
            <circle cx="13" cy="20" r="2.5" fill="currentColor" />
          </svg>
        </span>
        <span class="kds-nav__brand-name">{{ logoLabel }}</span>
      </a>

      <ul class="kds-nav__links">
        <li v-for="link in links" :key="link.label">
          <a class="kds-nav__link" :href="link.href">{{ link.label }}</a>
        </li>
      </ul>

      <div class="kds-nav__actions">
        <slot name="actions-extra" />
        <KdsButton
          v-if="ctaSecondary"
          variant="ghost"
          size="md"
          :disabled="ctaSecondaryDisabled"
          @click="$emit('cta-secondary-click', $event)"
        >
          {{ ctaSecondary }}
        </KdsButton>
        <KdsButton
          v-if="ctaPrimary"
          variant="primary"
          size="md"
          :disabled="ctaPrimaryDisabled"
          @click="$emit('cta-primary-click', $event)"
        >
          {{ ctaPrimary }}
        </KdsButton>
      </div>

      <button
        v-if="!hideMobileMenu"
        type="button"
        class="kds-nav__menu-toggle"
        :aria-expanded="open ? 'true' : 'false'"
        aria-controls="kds-nav-drawer"
        @click="toggleOpen"
      >
        <KdsIcon :name="open ? 'x' : 'menu'" :size="20" label="Menu" />
      </button>
    </div>

    <div
      v-if="open"
      id="kds-nav-drawer"
      class="kds-nav__drawer"
    >
      <ul class="kds-nav__drawer-links">
        <li v-for="link in links" :key="link.label">
          <a class="kds-nav__drawer-link" :href="link.href" @click="open = false">{{ link.label }}</a>
        </li>
      </ul>
      <div class="kds-nav__drawer-actions">
        <slot name="drawer-extra" />
        <KdsButton
          v-if="ctaSecondary"
          variant="ghost"
          size="md"
          :disabled="ctaSecondaryDisabled"
          @click="onDrawerSecondary"
        >
          {{ ctaSecondary }}
        </KdsButton>
        <KdsButton
          v-if="ctaPrimary"
          variant="primary"
          size="md"
          :disabled="ctaPrimaryDisabled"
          @click="onDrawerPrimary"
        >
          {{ ctaPrimary }}
        </KdsButton>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';

interface NavLink {
  label: string;
  href: string;
}

interface Props {
  logoLabel: string;
  links: NavLink[];
  brandHref?: string;
  ctaPrimary?: string;
  ctaSecondary?: string;
  ctaPrimaryDisabled?: boolean;
  ctaSecondaryDisabled?: boolean;
  hideMobileMenu?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  brandHref: '/',
  ctaPrimary: undefined,
  ctaSecondary: undefined,
  ctaPrimaryDisabled: false,
  ctaSecondaryDisabled: false,
  hideMobileMenu: false
});

const emit = defineEmits<{
  'cta-primary-click': [event: MouseEvent];
  'cta-secondary-click': [event: MouseEvent];
}>();

const open = ref(false);
function toggleOpen() {
  open.value = !open.value;
}

function onDrawerPrimary(event: MouseEvent) {
  if (props.ctaPrimaryDisabled) return;
  emit('cta-primary-click', event);
  open.value = false;
}

function onDrawerSecondary(event: MouseEvent) {
  if (props.ctaSecondaryDisabled) return;
  emit('cta-secondary-click', event);
  open.value = false;
}
</script>

<style scoped lang="scss">
.kds-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--paper);
  border-bottom: var(--border);

  &__inner {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    padding: var(--space-3) var(--space-7);
    max-width: 1280px;
    margin: 0 auto;
  }

  &__brand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    text-decoration: none;
    color: var(--ink);
  }

  &__brand-name {
    font-size: var(--fs-body);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.02em;
  }

  &__links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: none;
    align-items: center;
    gap: var(--space-6);
    flex: 1;
    justify-content: center;

    @media (min-width: 960px) {
      display: flex;
    }
  }

  &__link {
    color: var(--stone);
    text-decoration: none;
    font-size: var(--fs-body-sm);
    transition: color var(--d-fast) var(--ease-out);

    &:hover {
      color: var(--ink);
    }
  }

  &__actions {
    display: none;
    align-items: center;
    gap: var(--space-3);
    margin-left: auto;

    @media (min-width: 960px) {
      display: inline-flex;
    }
  }

  &__menu-toggle {
    margin-left: auto;
    background: transparent;
    border: none;
    color: var(--ink);
    padding: var(--space-2);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-2);
    }

    @media (min-width: 960px) {
      display: none;
    }
  }

  &__drawer {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5) var(--space-7);
    background: var(--paper);
    border-bottom: var(--border);

    @media (min-width: 960px) {
      display: none;
    }
  }

  &__drawer-links {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__drawer-link {
    color: var(--ink);
    text-decoration: none;
    font-size: var(--fs-h3);
  }

  &__drawer-actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
}
</style>
